import { React, Fragment, useState, useEffect } from "react";
import Navbar from "../../Components/Navbar";
import Topbar from "../../Components/Topbar";
import { Dialog, Transition } from "@headlessui/react";
import { useParams } from "react-router-dom";
import { sendGetRequest, sendPostRequest, useGetRequest } from "../../Client";
import ReactLoading from "react-loading";
import { Notyf } from "notyf";
import "notyf/notyf.min.css"; // for React, Vue and Svelte
import Swal from "sweetalert2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DepartmentAdd from "./add";
import DepartmentUpdate from "./update";

function Departments() {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const queryClient = useQueryClient();

  const [departmentID, setDepartmentID] = useState();

  // confirm delete
  const areYouSure = (id) => {
    Swal.fire({
      title: "Are you sure?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#de3333",
    }).then((result) => {
      if (result.isConfirmed) {
        doDeleteMutation(id);
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  // confirmation notifications
  const notyf = new Notyf({
    position: {
      x: "right",
      y: "top",
    },
  });

  const updatedNotyf = () => {
    notyf.success("Department updated");
  };

  const addedNotyf = () => {
    notyf.success("Department added");
  };

  const deletedNotyf = () => {
    notyf.success({ message: "Department deleted", background: "#FF0000" });
  };

  const errorNotyf = () => {
    notyf.success({
      message: "Error",
      background: "#FF0000",
    });
  };

  // loading type piece
  const { data: departments, isLoading } = useQuery({
    queryFn: () => sendGetRequest("departments", {}),
    queryKey: ["departments"],
  });

  // deleting type piece
  const departmentDelete = async (id) => {

    console.log("Deleting department with ID:", id);

    const res = await sendPostRequest("department/delete", {
      departmentID: id,
    });
  };

  const { mutateAsync: doDeleteMutation } = useMutation({
    mutationFn: (id) => {
      return departmentDelete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      deletedNotyf();
    },
  });

  return (
    <>
      <div className="flex flex-row w-full">
        <Navbar />

        <div className="flex flex-col w-full bg-gray-100 ml-60">
          <Topbar />

          <nav className="flex ml-10 mt-5 h-10" aria-label="Breadcrumb">
            <ol
              role="list"
              className="flex space-x-4 rounded-md bg-white px-6 shadow"
            >
              <li className="flex">
                <div className="flex items-center">
                  <a
                    href="/support-files"
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span>Back</span>
                  </a>
                </div>
              </li>
            </ol>
          </nav>

          <div className="bg-white px-4 sm:px-6 lg:px-8 border border-gray-200 ml-10 mr-10 mt-10 mb-40">
            <form>
              <div className="space-y-12">
                <div className="mt-10">
                  <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                      <h1 className="text-2xl font-semibold leading-6 text-gray-900 mb-5">
                        Departments
                      </h1>
                    </div>
                    <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                      <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="block rounded-md bg-green-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                      >
                        Add Department
                      </button>
                    </div>
                  </div>
                  <div className="mt-8 flow-root mb-10">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-0"
                              >
                                Customer
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                              >
                                Department Name
                              </th>

                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                              ></th>
                            </tr>
                          </thead>
                          {!isLoading ? (
                            departments.departments?.length > 0 ? (
                              <tbody className="divide-y divide-gray-200">
                                {departments.departments?.map((item) => (
                                  <tr key={item.departmentID}>
                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-700 sm:pl-0">
                                      {item.customerName}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                      {item.departmentName}
                                    </td>

                                    <td className="block text-right">
                                      <span className="mt-2 isolate inline-flex rounded-md shadow-sm">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setDepartmentID(item.departmentID);
                                            setEditOpen(true);
                                          }}
                                          className="relative inline-flex items-center rounded-l-md bg-amber-500 hover:bg-amber-400 focus-visible:outline-amber-600 px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-gray-300 focus:z-10"
                                        >
                                          Edit
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (areYouSure(item.departmentID)) {
                                              departmentDelete(item.departmentID);
                                            }
                                          }}
                                          className="relative -ml-px inline-flex items-center rounded-r-md bg-red-500 px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-gray-300 hover:bg-red-400 focus:z-10"
                                        >
                                          Delete
                                        </button>
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            ) : (
                              <>
                                <tbody className="divide-y divide-gray-200">
                                  <tr>
                                    <td
                                      colSpan="4"
                                      className="w-full text-center whitespace-nowrap px-3 py-4 text-sm text-gray-500 col-span-6"
                                    >
                                      -- No departments found --
                                    </td>
                                  </tr>
                                </tbody>
                              </>
                            )
                          ) : (
                            <>
                              <tbody className="divide-y divide-gray-200">
                                <tr>
                                  <td
                                    colSpan="5"
                                    className="w-full text-center whitespace-nowrap px-3 py-4 text-sm text-gray-500 col-span-6"
                                  >
                                    <ReactLoading
                                      type={"spin"}
                                      color="#5046e5"
                                    />
                                  </td>
                                </tr>
                              </tbody>
                            </>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <DepartmentAdd
        open={open}
        setOpen={setOpen}
        addedNotyf={addedNotyf}
        errorNotyf={errorNotyf}
        customerID={0}
        queryClient={queryClient}
      />
      
      {departmentID && (
        <DepartmentUpdate
          editOpen={editOpen}
          setEditOpen={setEditOpen}
          updatedNotyf={updatedNotyf}
          errorNotyf={errorNotyf}
          customerID={0}
          queryClient={queryClient}
          departmentID={departmentID}
        />
      )}
    </>
  );
}

export default Departments;
